import { useState, useEffect, useRef } from 'react';
import { C } from '../../../lib/tokens.js';
import { t } from '../../../lib/data.js';
import { isAgentRole } from '../../../lib/utils.js';
import { apiFetch } from '../../../lib/api.js';
import { useLookups } from '../../../lib/LookupsContext.jsx';
import { useExtensionSlot } from '../../../lib/ExtensionSlotsContext.jsx';
import { useViewport } from '../../../lib/useViewport.js';
import { Button, IconButton } from '../../../components/ui/Button.jsx';
import { Field, SearchableSelect } from '../../../components/ui/Fields.jsx';
import { Chip, TypeChip, PriorityChip, ModuleChip, findLookup, useResilientLookup } from '../../../components/ui/Chips.jsx';
import { Avatar } from '../../../components/ui/Avatar.jsx';
import { billingMeta } from '../../../components/ui/BillingMarker.jsx';
import { AttachmentThumbs } from './AttachmentThumbs.jsx';
import { useContactsForBPartner } from './shared.jsx';
import { RefreshableLabel } from './RefreshableLabel.jsx';

// ─── Chat intake ──────────────────────────────────────────────────────────────
export function ChatIntake({ onCancel, onSubmit, submitting, draft, update, role, me, onFindSimilar, onReply }) {
  const { requestTypes, modules: ctxModules, priorities, bpartners: bpartnersFromContext, requestClusters } = useLookups();
  // Same resilient-lookup + tolerant-match pair ModuleChip itself uses (see DetailPanel.jsx) -
  // ctxModules can be undefined before the app-boot lookup fetch resolves, and a plain
  // `modules.find(...)` crashed the whole page the first time this ran into that.
  const modules = useResilientLookup(ctxModules, '/lookups/modules');
  const { isMobile } = useViewport();
  const isAgent = isAgentRole(role, me);
  // Optional fieldset an extension can add (e.g. afsp.afts invoicing fields).
  const ExtraFields = useExtensionSlot('request.create.extraFields');
  const [contacts, refetchContacts] = useContactsForBPartner(draft.bpartnerId);
  // Business partners come from the app-wide lookups context (fetched once on
  // boot) - a local override lets the refresh button pull a freshly-added one
  // in without needing a full page reload (which would lose this draft).
  const [bpartnersOverride, setBpartnersOverride] = useState(null);
  const bpartners = bpartnersOverride ?? bpartnersFromContext;
  const refetchBpartners = () => {
    apiFetch('/lookups/bpartners')
      .then(data => setBpartnersOverride(Array.isArray(data) ? data : (data?.data ?? [])))
      .catch(() => {});
  };
  // bpartnerId starts null (untouched) — '' is the explicit "own organization" choice —
  // so an agent must pick one or the other before continuing past this step. A contact
  // is only meaningful (and required) once a real business partner is chosen.
  const bpartnerOptions = [{ id: "", name: t("create.field.bpartner.own_org") }, ...bpartners];
  const contactRequired = isAgent && !!draft.bpartnerId;
  const contextValid = (!isAgent || draft.bpartnerId !== null) && (!contactRequired || draft.contactId)
    && draft.extraValid !== false;
  const [step, setStep] = useState(() =>
    draft.type ? (draft.summary ? (draft.description ? 3 : 2) : 1) : 0
  );
  const messagesRef = useRef();
  // Internal staff only: the check lists other customers' requests (the API answers 403 for customers)
  const check = useSimilarCheck(isAgent ? onFindSimilar : null, draft, update);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (messagesRef.current) messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
  }, [step, check.similar, check.checking]);

  const goToStep = (n) => setStep(n);
  const advance = () => goToStep(step + 1);
  const goBack = () => { check.close(); goToStep(Math.max(0, step - 1)); };

  // Duplicate check (staff only) once summary and description are known: similar open tickets
  // are offered to link to or reply to; with none, or for customers, it moves straight on.
  const afterDescription = async () => {
    if (!draft.description.trim()) return;
    const list = await check.run();
    if (!list.length) advance();
  };
  const linkTo = (r) => { update('relatedTo', { id: r.id, summary: r.summary, docNumber: r.docNumber }); check.close(); advance(); };
  const skipSimilar = () => { update('relatedTo', null); check.close(); advance(); };
  const replyTo = async (r, confidentialType) => {
    setBusy(true);
    try { await onReply?.(r.id, draft, confidentialType); } finally { setBusy(false); }
  };

  const messages = [];
  messages.push({ from: 'agent', text: t('create.chat.intro') });
  if (step >= 1 && draft.type) {
    messages.push({ from: 'user',  text: requestTypes.find(x => x.id === draft.type)?.name });
    messages.push({ from: 'agent', text: 'Got it. In one short sentence, what should the helpdesk see in their inbox?' });
  }
  if (step >= 2 && draft.summary) {
    messages.push({ from: 'user',  text: draft.summary });
    messages.push({ from: 'agent', text: 'Thanks. Tell me a bit more — what did you do, what did you expect, what happened? Paste screenshots directly into the box, or attach files.' });
  }
  if (step === 2 && (check.checking || check.similar)) {
    messages.push({ from: 'user',  text: draft.description, files: draft.attachments });
    messages.push({ from: 'agent', text: t(check.checking ? 'create.similar.checking' : 'create.similar.found') });
  }
  if (step >= 3 && draft.description) {
    messages.push({
      from: 'user',
      text: draft.description,
      files: draft.attachments
    });
    if (draft.relatedTo) messages.push({ from: 'user', text: `${t('create.similar.linked')}: ${draft.relatedTo.summary}` });
    messages.push({ from: 'agent', text: 'How urgent is this for you?' });
  }
  if (step >= 4 && draft.priority) {
    messages.push({ from: 'user',  text: priorities.find(x => x.id === draft.priority)?.name + ' priority' });
    messages.push({ from: 'agent', text: 'Last thing — which module is this in, and is there a related document number (invoice, sales order, etc.)? You can skip if not relevant.' });
  }
  if (step >= 5) {
    messages.push({ from: 'user',  text: `${findLookup(modules, draft.module)?.name || 'No module'}${draft.docNumber ? ` · doc ${draft.docNumber}` : ''}` });
    messages.push({ from: 'agent', text: "Perfect. Here's a summary — review and submit when ready." });
  }

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: isMobile ? '16px 12px' : '20px 24px', display: 'flex', flexDirection: 'column', gap: 16, minHeight: 'calc(100vh - 200px)' }}>
      <ChatStepper step={step} isMobile={isMobile} />

      <div
        ref={messagesRef}
        style={{
          background: C.tin,
          border: `1px solid ${C.mercury}`,
          borderRadius: 6,
          padding: 18,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          maxHeight: 480,
          overflowY: 'auto'
        }}
      >
        {messages.map((m, i) => (
          <ChatBubble key={i} from={m.from} text={m.text} files={m.files} />
        ))}
        {step === 5 ? <ChatReview billingType={findLookup(requestTypes, draft.type)?.billing_type} draft={draft} isAgent={isAgent} bpartners={bpartners} contacts={contacts} requestClusters={requestClusters} /> : null}
      </div>

      <div style={{ background: C.tin, border: `1px solid ${C.mercury}`, borderRadius: 6, padding: 14 }}>
        {step > 0 && step < 5 && (
          <button type="button" onClick={goBack}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4, marginBottom: 10,
              border: 'none', background: 'none', padding: 0, cursor: 'pointer',
              color: C.iron, fontSize: 12, fontFamily: 'Roboto',
            }}>
            <span className="material-icons" style={{ fontSize: 15 }}>arrow_back</span>
            {t('create.back')}
          </button>
        )}
        {step === 0 && (
          <div>
            <div style={{ fontSize: 12, color: C.iron, marginBottom: 10 }}>{t('create.type.help')}</div>
            {/* Flex-wrap: cards of a row share the row height, and the cards of a short last row grow to fill it */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {requestTypes.map(o => (
                <button key={o.id} onClick={() => { update('type', o.id); advance(); }} type="button"
                  style={{ flex: '1 1 200px', minWidth: 0, boxSizing: 'border-box', background: C.tin, border: `1px solid ${C.mercury}`, borderRadius: 6, padding: 12, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4, cursor: 'pointer', textAlign: 'left', fontFamily: 'Roboto', transition: 'all 120ms' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = C.liquid; e.currentTarget.style.background = C.rowActive; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = C.mercury; e.currentTarget.style.background = C.tin; }}>
                  <span className="material-icons" style={{ fontSize: 22, color: C.liquid }}>{o.icon}</span>
                  <span style={{ fontSize: 13, fontWeight: 500, color: C.fg }}>{o.name}</span>
                  {o.hint ? <span style={{ fontSize: 11, color: C.iron, lineHeight: 1.4, whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>{o.hint}</span> : null}
                  <BillingLine billingType={o.billing_type} />
                </button>
              ))}
            </div>
          </div>
        )}
        {step === 1 && (
          <ComposerLine placeholder={t('create.field.summary.help')} value={draft.summary}
            onChange={v => update('summary', v)} onSubmit={() => { if (draft.summary.trim().length >= 8) advance(); }}
            label="Summary" minLength={8} maxLength={60} />
        )}
        {step === 2 && check.similar && (
          <SimilarPanel items={check.similar} isAgent={isAgent} busy={busy} onLink={linkTo} onReply={replyTo} onSkip={skipSimilar} />
        )}
        {step === 2 && !check.similar && (
          <ComposerLine placeholder={t('create.field.description.help')} value={draft.description}
            onChange={v => update('description', v)} onSubmit={() => { if (!check.checking) afterDescription(); }}
            multiline withAttach label="Description"
            files={draft.attachments}
            onFiles={v => update('attachments', v)} />
        )}
        {step === 3 && (
          <div>
            <div style={{ fontSize: 12, color: C.iron, marginBottom: 10 }}>{t('create.field.priority.help')}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
              {priorities.map(p => (
                <button key={p.id} type="button" onClick={() => { update('priority', p.id); advance(); }}
                  style={{ background: C.tin, border: `1px solid ${C.mercury}`, borderRadius: 6, padding: 10, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4, fontFamily: 'Roboto' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = p.color; e.currentTarget.style.background = '#F8F8F8'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = C.mercury; e.currentTarget.style.background = C.tin; }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color }} />
                  <span style={{ fontSize: 13, fontWeight: 500, color: C.fg }}>{p.name}</span>
                  <span style={{ fontSize: 11, color: C.iron }}>{p.description}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        {step === 4 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {isAgent && (
              <>
                <SearchableSelect label={<RefreshableLabel label={t('create.field.bpartner')} onRefresh={refetchBpartners} />} required value={draft.bpartnerId} onChange={v => update('bpartnerId', v)}
                  options={bpartnerOptions} placeholder={t('create.field.bpartner.placeholder')} helper={t('create.field.bpartner.help')} />
                {contactRequired && (
                  <SearchableSelect label={<RefreshableLabel label={t('create.field.contact')} onRefresh={refetchContacts} />} required value={draft.contactId} onChange={v => update('contactId', v)}
                    options={contacts} placeholder={t('create.field.contact.placeholder')} />
                )}
              </>
            )}
            <SearchableSelect label={t('create.field.module')} value={draft.module} onChange={v => update('module', v)}
              options={modules} placeholder="Pick a module…" />
            <Field label={t('create.field.doc')} value={draft.docNumber} onChange={v => update('docNumber', v)}
              placeholder="e.g. SO-2026-09122" helper={t('create.field.doc.help')} icon="description" />
            {isAgent && (
              <SearchableSelect label={t('create.field.cluster')} value={draft.clusterId} onChange={v => update('clusterId', v)}
                options={requestClusters} placeholder={t('create.field.cluster.placeholder')} helper={t('create.field.cluster.help')} />
            )}
            {isAgent && ExtraFields && (
              <ExtraFields me={me} value={draft.extra} onChange={v => update('extra', v)} onValidChange={v => update('extraValid', v)} />
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
              {!isAgent && <Button kind="ghost" onClick={advance}>Skip</Button>}
              <Button kind="primary" icon="arrow_forward" onClick={advance} disabled={!contextValid}>Continue</Button>
            </div>
          </div>
        )}
        {step === 5 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Button kind="subtle" onClick={onCancel} disabled={submitting}>{t('create.cancel')}</Button>
            <Button kind="ghost" icon="edit" onClick={() => goToStep(0)} disabled={submitting}>Edit</Button>
            <Button kind="primary" icon={submitting ? undefined : "send"} disabled={submitting || !contextValid} onClick={() => onSubmit?.(draft)}>
              {submitting ? t('create.submitting') : t('create.submit')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function ChatStepper({ step, isMobile = false }) {
  // done is purely step > id for every entry - consistently "have you moved past
  // this step", not "does the field have a value". Mixing the two meant Type/
  // Summary/Details stayed checkmarked forever once filled in, even after going
  // back to re-visit them, while Priority/Context correctly un-checked - going
  // back looked broken for the first three and fine for the other two.
  const steps = [
    { id: 0, label: 'Type',     done: step > 0 },
    { id: 1, label: 'Summary',  done: step > 1 },
    { id: 2, label: 'Details',  done: step > 2 },
    { id: 3, label: 'Priority', done: step > 3 },
    { id: 4, label: 'Context',  done: step > 4 },
    { id: 5, label: 'Review',   done: false },
  ];

  if (isMobile) {
    // The full pill strip has no wrap/scroll container of its own on a phone -
    // it was blowing out the whole page's width instead of just its own row.
    // A compact "Step X of N: Label" + thin progress bar needs no scrolling at all.
    const current = steps[step];
    return (
      <div>
        <div style={{ fontSize: 12, fontWeight: 500, color: C.deepsea, marginBottom: 6 }}>
          Step {step + 1} of {steps.length}: {current.label}
        </div>
        <div style={{ height: 4, borderRadius: 999, background: C.mercury, overflow: 'hidden' }}>
          <div style={{
            height: '100%', borderRadius: 999, background: C.liquid,
            width: `${((step + 1) / steps.length) * 100}%`, transition: 'width 150ms ease',
          }} />
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      {steps.map((s, i) => {
        const active = step === s.id;
        return (
          <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 999,
              background: active ? C.rowActive : (s.done ? C.tin : 'transparent'),
              border: `1px solid ${active ? C.liquid : (s.done ? C.mercury : 'transparent')}`,
              fontSize: 11, fontWeight: 500, color: active ? C.deepsea : (s.done ? C.fg : C.silver),
            }}>
              {s.done ? (
                <span className="material-icons" style={{ fontSize: 14, color: C.patina }}>check_circle</span>
              ) : (
                <span style={{
                  width: 16, height: 16, borderRadius: '50%',
                  background: active ? C.liquid : 'transparent',
                  border: `1.5px solid ${active ? C.liquid : C.silver}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 10, color: '#fff', fontWeight: 600,
                }}>{active ? s.id + 1 : ''}</span>
              )}
              {s.label}
            </div>
            {i < steps.length - 1 ? <span style={{ width: 12, height: 1, background: C.mercury }} /> : null}
          </div>
        );
      })}
    </div>
  );
}

function ChatBubble({ from, text, files }) {
  if (from === 'agent') {
    return (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ width: 28, height: 28, borderRadius: '50%', background: C.deepsea, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
          <span className="material-icons" style={{ fontSize: 16 }}>support_agent</span>
        </div>
        <div style={{ background: C.platinum, border: `1px solid ${C.mercury}`, borderRadius: '6px 6px 6px 1px', padding: '8px 12px', fontSize: 13, color: C.fg, lineHeight: 1.5, maxWidth: '80%' }}>{text}</div>
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, justifyContent: 'flex-end' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end', maxWidth: '80%' }}>
        <div style={{ background: C.deepsea, color: '#fff', borderRadius: '6px 6px 1px 6px', padding: '8px 12px', fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap', width: '100%' }}>{text}</div>
        {files && files.length > 0 && (
          <div style={{ background: C.tin, border: `1px solid ${C.mercury}`, borderRadius: 4, padding: 6, alignSelf: 'flex-end' }}>
            <AttachmentThumbs files={files} />
          </div>
        )}
      </div>
      <Avatar user="me" size={28} />
    </div>
  );
}

function ComposerLine({ placeholder, value, onChange, onSubmit, multiline, withAttach, label, files, onFiles, minLength = 0, maxLength }) {
  const inputRef = useRef();
  const fileInputRef = useRef();
  const [dragOverComposer, setDragOverComposer] = useState(false);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !multiline) { e.preventDefault(); onSubmit(); }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && multiline) { e.preventDefault(); onSubmit(); }
  };

  const handlePaste = withAttach ? (e) => {
    const imgs = Array.from(e.clipboardData?.items || [])
      .filter(it => it.kind === 'file' && it.type.startsWith('image/'))
      .map(it => it.getAsFile()).filter(Boolean);
    if (imgs.length) { e.preventDefault(); onFiles?.([...(files || []), ...imgs]); }
  } : undefined;

  const addFiles = (fileList) => {
    const arr = Array.from(fileList || []);
    if (arr.length) onFiles?.([...(files || []), ...arr]);
  };

  const removeFile = (i) => {
    const next = [...(files || [])]; next.splice(i, 1); onFiles?.(next);
  };

  const hasFiles = withAttach && files?.length > 0;

  return (
    <div>
      <div style={{ fontSize: 11, color: C.iron, marginBottom: 6, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>

      {/* Target input block for drag events */}
      <div
        onDragOver={e => {
          if (withAttach) { e.preventDefault(); setDragOverComposer(true); }
        }}
        onDragLeave={e => {
          if (withAttach && !e.currentTarget.contains(e.relatedTarget)) setDragOverComposer(false);
        }}
        onDrop={e => {
          if (withAttach) {
            e.preventDefault();
            setDragOverComposer(false);
            if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
          }
        }}
        style={{
          position: 'relative',
          border: `1px dashed ${dragOverComposer ? C.liquid : C.mercury}`,
          borderRadius: 4,
          background: dragOverComposer ? C.rowActive : C.tin,
          transition: 'all 120ms ease-in-out'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, padding: 8 }}>
          {multiline ? (
            <textarea ref={inputRef} value={value} onChange={e => onChange(e.target.value)} onKeyDown={onKey} onPaste={handlePaste}
              placeholder={placeholder} rows={3}
              style={{ flex: 1, border: 'none', outline: 'none', fontSize: 13, fontFamily: 'Roboto', resize: 'vertical', lineHeight: 1.5, color: C.fg, background: 'transparent' }} />
          ) : (
            <input ref={inputRef} value={value} onChange={e => onChange(e.target.value)} onKeyDown={onKey}
              placeholder={placeholder} maxLength={maxLength}
              style={{ flex: 1, border: 'none', outline: 'none', fontSize: 13, fontFamily: 'Roboto', color: C.fg }} />
          )}
          {withAttach && (
            <>
              <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }}
                onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
              <IconButton icon="attach_file" title="Attach files" onClick={() => fileInputRef.current.click()} />
            </>
          )}
          <Button kind="primary" icon="send" size="sm" onClick={onSubmit} disabled={value.trim().length < Math.max(minLength, 1)}>Continue</Button>
        </div>
        {hasFiles && (
          <div style={{ padding: '0 8px 8px' }}>
            <AttachmentThumbs files={files} onRemove={removeFile} />
          </div>
        )}

        {/* Localized Drag & Drop Prompt Tint Overlay inside the Box */}
        {dragOverComposer && (
          <div style={{
            position: 'absolute', inset: 0, background: 'rgba(34, 52, 105, 0.05)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 6, borderRadius: 4, pointerEvents: 'none'
          }}>
            <span className="material-icons" style={{ fontSize: 16, color: C.liquid }}>cloud_upload</span>
            <span style={{ fontSize: 12, fontWeight: 500, color: C.deepsea }}>Drop files directly inside the input zone</span>
          </div>
        )}
      </div>
      <div style={{ fontSize: 10, color: C.silver, marginTop: 4 }}>
        {multiline ? 'Press ⌘+Enter to continue · paste images directly' : 'Press Enter to continue'}
        {maxLength ? ` · ${value.trim().length}/${maxLength}${minLength ? `, ${minLength} minimum` : ''}` : ''}
      </div>
    </div>
  );
}

// Small billing line under a request type: icon + short text (nothing for internal/unknown).
function BillingLine({ billingType }) {
  const meta = billingMeta(billingType);
  const text = billingType === 'F' ? t('billing.F.long') : meta ? t(`billing.${billingType}.long`) : null;
  if (!text) return null;
  return (
    <span style={{ marginTop: 'auto', paddingTop: 6, display: 'inline-flex', alignItems: 'flex-start', gap: 4, fontSize: 11, color: meta?.color ?? C.patina, fontWeight: 500, lineHeight: 1.3 }}>
      <span className="material-icons" style={{ fontSize: 13 }}>{meta?.icon ?? 'check_circle'}</span>
      {text}
    </span>
  );
}

function ChatReview({ billingType, draft, isAgent, bpartners, contacts, requestClusters }) {
  const bpartnerLabel = draft.bpartnerId === ""
    ? t("create.field.bpartner.own_org")
    : draft.bpartnerId
      ? (bpartners?.find(b => b.id === draft.bpartnerId)?.name ?? draft.bpartnerId)
      : null;
  const contactLabel = draft.contactId
    ? (contacts?.find(c => c.id === draft.contactId)?.name ?? draft.contactId)
    : null;
  const clusterLabel = draft.clusterId
    ? (requestClusters?.find(c => c.id === draft.clusterId)?.name ?? draft.clusterId)
    : null;

  return (
    <div style={{ marginTop: 4, background: C.platinum, border: `1px solid ${C.mercury}`, borderRadius: 6, padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 500, color: C.iron, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Review</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <TypeChip id={draft.type} size="sm" readOnly />
        <PriorityChip id={draft.priority} size="sm" readOnly />
        {isAgent && bpartnerLabel ? <Chip bg={C.tin} color={C.fg} icon="business" size="sm">{bpartnerLabel}</Chip> : null}
        {isAgent && contactLabel ? <Chip bg={C.tin} color={C.fg} icon="person" size="sm">{contactLabel}</Chip> : null}
        {draft.module    ? <ModuleChip id={draft.module} size="sm" readOnly /> : null}
        {draft.docNumber ? <Chip bg={C.tin} color={C.fg} icon="description" size="sm">{draft.docNumber}</Chip> : null}
        <RelatedChip related={draft.relatedTo} />
        {isAgent && clusterLabel ? <Chip bg={C.tin} color={C.fg} icon="hub" size="sm">{clusterLabel}</Chip> : null}
        {draft.attachments.length > 0 ? (
          <Chip bg={C.tin} color={C.fg} icon="attach_file" size="sm">
            {draft.attachments.length} file{draft.attachments.length !== 1 ? 's' : ''}
          </Chip>
        ) : null}
      </div>
      {/* An agent filing for the own organization (no business partner picked) gets no quote */}
      {(billingType === 'Q' || billingType === 'S') && !(isAgent && !draft.bpartnerId) ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.fg }}>
          <span className="material-icons" style={{ fontSize: 15, color: billingMeta(billingType)?.color }}>hourglass_top</span>
          {t('create.review.quote_note')}
        </div>
      ) : null}
      <div style={{ fontSize: 14, color: C.fg, fontWeight: 500, lineHeight: 1.4 }}>{draft.summary}</div>
      <div style={{ fontSize: 12, color: C.iron, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{draft.description}</div>
      {draft.attachments.length > 0 && (
        <AttachmentThumbs files={draft.attachments} />
      )}
    </div>
  );
}

// ─── Duplicate check ──────────────────────────────────────────────────────────

// Runs once summary + description are known. Returns the list of similar open
// tickets; the caller decides whether to show SimilarPanel or move on.
function useSimilarCheck(onFindSimilar, draft, update) {
  const [similar, setSimilar] = useState(null); // null = panel hidden
  const [checking, setChecking] = useState(false);

  const run = async () => {
    setChecking(true);
    const list = (await onFindSimilar?.(draft)) ?? [];
    setChecking(false);
    // Keep an earlier pick only if it is still among the matches.
    if (draft.relatedTo && !list.some(r => r.id === draft.relatedTo.id)) update('relatedTo', null);
    setSimilar(list.length ? list : null);
    return list;
  };

  return { similar, checking, run, close: () => setSimilar(null) };
}

function SimilarPanel({ items, isAgent, busy, onLink, onReply, onSkip }) {
  const [internal, setInternal] = useState(true);
  const hiddenOthers = !isAgent && items.some(r => !r.canView);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, color: C.fg, lineHeight: 1.5 }}>
        <span className="material-icons" style={{ fontSize: 18, color: C.liquid }}>manage_search</span>
        <div>
          {t('create.similar.found')}
          {hiddenOthers && <div style={{ fontSize: 11, color: C.iron, marginTop: 2 }}>{t('create.similar.privacy')}</div>}
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {items.map(r => (
          <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '8px 10px', border: `1px solid ${C.mercury}`, borderRadius: 4, background: C.platinum }}>
            {r.docNumber
              ? <span style={{ fontFamily: 'Roboto Mono', fontSize: 11, color: C.liquid }}>#{r.docNumber}</span>
              : <span className="material-icons" style={{ fontSize: 16, color: C.silver }}>lock</span>}
            <span style={{ flex: 1, minWidth: 160, fontSize: 13, color: C.fg, fontWeight: 500 }}>{r.summary}</span>
            {isAgent && <Button kind="ghost" size="sm" icon="reply" disabled={busy} onClick={() => onReply(r, internal ? 'I' : 'A')}>{t('create.similar.reply')}</Button>}
            <Button kind="outline" size="sm" icon="add_link" disabled={busy} onClick={() => onLink(r)}>{t('create.similar.link')}</Button>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {isAgent && (
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: C.iron, cursor: 'pointer' }}>
            <input type="checkbox" checked={internal} onChange={e => setInternal(e.target.checked)} />
            {t('create.similar.internal')}
          </label>
        )}
        <div style={{ flex: 1 }} />
        <Button kind="primary" icon="arrow_forward" disabled={busy} onClick={onSkip}>{t('create.similar.none')}</Button>
      </div>
    </div>
  );
}

function RelatedChip({ related }) {
  if (!related) return null;
  return (
    <Chip bg={C.tin} color={C.fg} icon="link" size="sm">
      {t('create.similar.linked')}: {related.docNumber ? `#${related.docNumber} ` : ''}{related.summary}
    </Chip>
  );
}
